import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, Outlet } from "react-router-dom";
import { setModalsData, TModalsState } from "../../api/store/commonSlice";
import svgIcons from "../../assets/svg/svgIcons";
import Accordian from "../../components/accordian";
import HeaderView from "../../components/admin/headerView";
import PagewideMessageDisplay from "../../components/pagewideMessageDisplay";
import { APP_CONFIG } from "../../constants/constants";
import { ACL_NAMES, useACL } from "../../hooks/acl";
import { routeUrls } from "../../navigation/routeUrls";
import { useLoggedInUserData } from "../../utils/user";
import { ACCOUNT_RECORDER_TYPE_VALUES } from "../admin-details/account/constants";
import { ROLE_ACCESS_LEVELS } from "../admin-details/role/constants";
import useVisionMax from "./useVisionMax";
import { useLocation } from "react-router-dom";
import { DeviceEvent } from "../../api/types/Camera";
import { RENAMED_EVENTS } from "./constants";

const sideMenu = {
  dashboard: "Dashboard",
  video_history: {
    events: "Events",
    playback: "Video Playback",
    files: "File Retrieval (Recordings)",
  },
  management: {
    camera_management: "Camera Management",
    camera_settings: "Camera Settings",
    sensor_event_detection: "Sensor Event Detection",
    ai_event_detction_adas: "AI Event Detection- ADAS",
    ai_event_detction_dms: "AI Event Detection- DMS",
    traffic_signal_detection: "Traffic Signs and Signal Detection",
  },
};

const configPageSlugs = [
  "camera_settings",
  "sensor_event_detection",
  "ai_event_detction_adas",
  "ai_event_detction_dms",
  "traffic_signal_detection",
];

const getUrlByname = (name: string) => {
  switch (name) {
    case sideMenu.dashboard:
      return routeUrls.dashboardChildren.cameraChildren.dashboard;
    case sideMenu.video_history.events:
      return routeUrls.dashboardChildren.cameraChildren.events;
    case sideMenu.video_history.playback:
      return routeUrls.dashboardChildren.cameraChildren.video_playback;
    case sideMenu.video_history.files:
      return routeUrls.dashboardChildren.cameraChildren.recordings;
    case sideMenu.management.camera_management:
      return routeUrls.dashboardChildren.cameraChildren.camera_management;
    case sideMenu.management.camera_settings:
      return routeUrls.dashboardChildren.cameraChildren.camera_settings;
    case sideMenu.management.sensor_event_detection:
      return routeUrls.dashboardChildren.cameraChildren.sensor_event_detection;
    case sideMenu.management.ai_event_detction_adas:
      return routeUrls.dashboardChildren.cameraChildren.ai_event_detection_adas;
        case sideMenu.management.ai_event_detction_dms:
      return routeUrls.dashboardChildren.cameraChildren.ai_event_detection_dms;
    case sideMenu.management.traffic_signal_detection:
      return routeUrls.dashboardChildren.cameraChildren
        .traffic_signal_detection;
  }
};

const SideMenu = (menuObj: { [key: string]: string | object } = {}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "camera",
  });
  const [isOpen, setIsOpen] = useState<{ [key: string]: boolean }>({});
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const dispatch = useDispatch();

  // collapse the main left navigation
  useEffect(() => {
    setTimeout(() => {
      dispatch(
        setModalsData({
          ...modalsState,
          isExpandedSideMenu: false,
        })
      );
    }, 200); // adding a delay of 200ms ensures collapse of menu
  }, []);

  // Get current URL path to determine active link
  const location = useLocation();

  // check if current location matches paths from configPageSlugs
  const isConfigPage = configPageSlugs.some((slug) => {
    const url = getUrlByname(
      sideMenu.management[slug as keyof typeof sideMenu.management] as string
    );
    if (!url) return false; // if no URL is found for the slug, skip it
    // Check if the current pathname includes the URL for the slug
    // This allows for nested paths, e.g., /camera/settings/ai_event_detection
    // or /camera/settings/traffic_signal_detection
    // return location.pathname.includes(getUrlByname(slug) || "")
    return location.pathname.includes(url);
  });
  // console.log("== isConfigPage", isConfigPage, "== location", location);

  const linkIsActive = (path: string, exactMatch: boolean = false) => {
    if (exactMatch) {
      return location.pathname === path;
    }
    // Handles both exact and nested matches
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  return (
    <>
      {Object.keys(menuObj).map((item, index) => {
        if (typeof menuObj[item] === "string") {
          const url = getUrlByname(menuObj[item] as string) || "";
          return (
            <Link
              className={
                "w-full px-5 py-[10px] flex items-center cursor-pointer" +
                " border-b border-gray-200 Xlast-of-type:border-b-0" +
                (linkIsActive(url, true) ? " bg-accent-blue-pale" : "")
              }
              to={url}
              key={`link-${index}`}
            >
              <span className="flex-grow text-left text-lg font-semibold leading-10 tracking-tighter text-accent-blue-dark">
                {menuObj[item] as string}
              </span>
            </Link>
          );
        } else if (typeof menuObj[item] === "object") {
          return (
            <Accordian
              title={t(`menuItems.${item}`)}
              openByDefault={Object.keys(menuObj[item] as object).some(
                (subItem) => {
                  const subUrl = getUrlByname(
                    (menuObj[item] as { [key: string]: string })[subItem]
                  );
                  return linkIsActive(subUrl as string);
                }
              )}
              key={`accordian-${index}`}
            >
              <div className="flex flex-col gap-2 pb-2">
                {Object.keys(menuObj[item])
                  .filter((menuSubItem) => {
                    if (item === "management" && !isConfigPage) {
                      if (configPageSlugs.includes(menuSubItem)) {
                        return false; // Skip config pages if not in config view
                      }
                    }
                    return menuSubItem;
                  })
                  .map((subItem, subIndex) => {
                    const subUrl = getUrlByname(
                      (typeof menuObj[item] === "object" &&
                      menuObj[item] !== null
                        ? (menuObj[item] as { [key: string]: string })[subItem]
                        : "") as string
                    ) as string;
                    const thisIsConfigPage = configPageSlugs.includes(
                      subItem as keyof typeof sideMenu.management
                    );
                    return (
                      <Link
                        className={
                          "ml-2 mr-0 px-3 py-2 rounded-md " +
                          (linkIsActive(subUrl)
                            ? "bg-accent-blue-pale cursor-default"
                            : thisIsConfigPage
                              ? "cursor-not-allowed"
                              : "hover:bg-accent-blue-paleO66")
                        }
                        to={subUrl}
                        onClick={(e) => {
                          if (thisIsConfigPage) {
                            e.preventDefault();
                            e.stopPropagation();
                          }
                        }}
                        key={`link-${subIndex}`}
                      >
                        <span>
                          {
                            (typeof menuObj[item] === "object" &&
                            menuObj[item] !== null
                              ? (menuObj[item] as { [key: string]: string })[
                                  subItem
                                ]
                              : "") as string
                          }
                        </span>
                      </Link>
                    );
                  })}
              </div>
            </Accordian>
          );
        }
      })}
    </>
  );
};

const ScreenCameraAdmin = () => {
  const location = useLocation();
  const deviceEventFromState = location?.state as DeviceEvent;

  const { t } = useTranslation("translation", {
    keyPrefix: "camera",
  });
  const { getUserACLAccessLevel } = useACL();
  const thisOrgCameraRecorderConfigAllowed = useLoggedInUserData(
    "cameraRecorderConfigAllowed"
  );

  const visibleAdminSections = [
    "dashboard",
    "video_history",
    ...(getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_CAMERA_CONFIG) >=
      ROLE_ACCESS_LEVELS.READ &&
    parseInt(thisOrgCameraRecorderConfigAllowed || "0") === 1
      ? ["configuration", "management"]
      : []),
  ];

  const { vmToken, isFetchingVisionMaxGetToken } = useVisionMax();

  // surfsight / visionmax check
  const thisOrgViewVideoAllowed = useLoggedInUserData("viewVideoAllowed");
  const thisOrgCameraRecorderType = useLoggedInUserData("cameraRecorderType");

  if (thisOrgViewVideoAllowed !== "1") {
    return (
      <PagewideMessageDisplay
        title="Forbidden"
        description="You are not allowed to access this section."
      />
    );
  }

  // surfsight camera admin (iframe)
  if (thisOrgCameraRecorderType === ACCOUNT_RECORDER_TYPE_VALUES.SURFSIGHT) {
    return (
      <>
        <div
          className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-2 min-h-screen-excl-header Xbg-black/20 flex flex-col`}
        >
          <iframe
            className="flex-1 w-full border-0"
            src={APP_CONFIG.CAMERA_ADMIN_URL}
          />
        </div>
      </>
    );
  }

  // visionmax camera admin
  if (thisOrgCameraRecorderType === ACCOUNT_RECORDER_TYPE_VALUES.VISIONMAX) {
    if (isFetchingVisionMaxGetToken) {
      return (
        <div
          className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-2 min-h-screen-excl-header flex items-center justify-center`}
        >
          <img
            src={svgIcons.LoadingAnimation}
            alt="loading-animation"
            className="size-12"
          />
        </div>
      );
    } else if (!vmToken) {
      return (
        <div
          className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-2 min-h-screen-excl-header flex items-center justify-center`}
        >
          <PagewideMessageDisplay
            title="Connection failed"
            description="A connection with the cameras service could not be established."
          />
        </div>
      );
    }

    if (!!deviceEventFromState?.status?.description) {
      return (
        <>
          <HeaderView
            title={
              !!deviceEventFromState?.status?.description
                ? RENAMED_EVENTS.find(
                    (item) =>
                      item.backendValue ===
                      deviceEventFromState?.status?.description
                  )?.displayValue || deviceEventFromState?.status?.description
                : ""
            }
            showBackButton={true}
          />
          <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-4`}>
            <Outlet />
          </div>
        </>
      );
    }

    return (
      <>
        <HeaderView title={t("heading")} showBackButton={true} />
        <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-4`}>
          <div className="flex Xgrid Xgrid-cols-12 gap-6">
            <aside
              className={`min-w-72 max-w-72 Xcol-span-3 flex flex-col Xfixed h-full`}
            >
              <SideMenu
                // dashboard={"dashboard"}
                // video_history={"video_history"}
                // ...
                // restrict sections based on ACL via `visibleAdminSections`
                {...Object.keys(sideMenu)
                  .filter((key) => visibleAdminSections.includes(key))
                  .reduce((obj, key) => {
                    // @ts-ignore
                    obj[key] = sideMenu[key as keyof typeof sideMenu];
                    return obj;
                  }, {})}
              />
            </aside>

            <div className="flex-grow Xcol-span-9">
              <div className="flex flex-col w-full">
                <Outlet />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div
      className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-2 min-h-screen-excl-header flex items-center justify-center`}
    >
      <PagewideMessageDisplay
        title="Camera configuration not found"
        description="Please contact your account administrator."
      />
    </div>
  );
};
export default ScreenCameraAdmin;
